import { useTheme } from '@emotion/react';
import { memo, type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { getStudentTranscriptAssessmentLabel } from '@didaskill/domain/assignment-submissions/utils';
import { SUBMISSION_STATUSES } from '@didaskill/domain/assignments/constants';
import { OVERALL_AVERAGE_GRADING_SCALE } from '@didaskill/domain/courses/constants';
import { getTranscriptAssignmentsCount } from '@didaskill/domain/students/utils';
import type { StudentAcademicTranscript } from '@didaskill/domain/users/types';
import { Card, Typography } from '@didaskill/ui/design-system';
import {
  academicTranscriptMessages,
  globalMessages,
  gradeMessages,
  studentTranscriptAssessmentLevelMessages,
} from '@didaskill/ui/intl';

import messages from './AcademicTranscriptAssessment.messages';
import * as styles from './AcademicTranscriptAssessment.styles';

/**
 * Renders the `<Grade>` rich-text chunk of the overall average message.
 * @param chunks - Content wrapped by the `<Grade>` tag.
 * @returns The highlighted grade.
 */
const renderGrade = (chunks: ReactNode[]): ReactNode => (
  <Typography
    variant={Typography.VARIANTS.HEADING_3}
    element="span"
    css={styles.coloredTextSuccess}
  >
    {chunks}
  </Typography>
);

/**
 * Renders the `<GradingScale>` rich-text chunk as-is.
 * @param chunks - Content wrapped by the `<GradingScale>` tag.
 * @returns The unchanged chunks.
 */
const renderGradingScale = (chunks: ReactNode[]): ReactNode => chunks;

export const AcademicTranscriptAssessmentBase = ({
  transcript,
}: {
  transcript: StudentAcademicTranscript;
}) => {
  const theme = useTheme();

  const overallGradedAssignments = useMemo(
    () => getTranscriptAssignmentsCount(transcript, [SUBMISSION_STATUSES.CORRECTED]),
    [transcript],
  );

  return (
    <div css={styles.assessment}>
      <Card color={theme.colors.success[500]} css={styles.card}>
        <div css={styles.assessmentCardBody}>
          <div css={styles.cardItem}>
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
              <FormattedMessage {...academicTranscriptMessages.OVERALL_AVERAGE} />
            </Typography>

            <Typography variant={Typography.VARIANTS.HEADING_5} element="span">
              <FormattedMessage
                {...(Number.isFinite(transcript.overallAverage)
                  ? gradeMessages.GRADE
                  : globalMessages.NOT_AFFECTED)}
                values={{
                  grade: transcript.overallAverage,
                  gradingScale: OVERALL_AVERAGE_GRADING_SCALE,
                  Grade: renderGrade,
                  GradingScale: renderGradingScale,
                }}
              />
            </Typography>
          </div>

          <div css={styles.cardItem}>
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
              <FormattedMessage {...messages.ASSESSMENT_LABEL} />
            </Typography>

            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
              <p>
                <FormattedMessage
                  {...messages.ASSIGNMENTS_COMPLETED}
                  values={{
                    value: overallGradedAssignments.count / overallGradedAssignments.total,
                  }}
                />
              </p>
              {Number.isFinite(transcript?.overallAverage) && (
                <p>
                  <FormattedMessage
                    {...getStudentTranscriptAssessmentLabel(
                      transcript.overallAverage,
                      studentTranscriptAssessmentLevelMessages,
                    )}
                  />
                </p>
              )}
            </Typography>
          </div>
        </div>
      </Card>
    </div>
  );
};

AcademicTranscriptAssessmentBase.displayName = 'AcademicTranscriptAssessment';

export const AcademicTranscriptAssessment = memo(AcademicTranscriptAssessmentBase);
